import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { processWithAi } from "../../utils/API_SERVICE";
import { useMDTSpeech } from "./hooks/useMDTSpeech";
import LanguageDropdown from "../../components/common/LanguageDropdown";
import micwhite from "../../assets/icons/mic-white.png";
import { DICTATION_LANGUAGES } from "../../constants/languages";

function MDTRecording({
  studyId,
  language,
  onInsertIntoReport,
}) {
  const { t } = useTranslation("common");
  const [generatedReport, setGeneratedReport] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState(language || "en-US");

  // Determine RTL based on both UI language and dictation language - checks if either is Hebrew or Arabic
  const isRTL =
    language === "he" ||
    language === "ar" ||
    currentLanguage?.startsWith("he") ||
    currentLanguage?.startsWith("ar");

  const {
    segments,
    streamingSegment,
    isRecording,
    isConnecting,
    error,
    connectAndStart,
    stopRecording,
    sendAudioChunk,
    clearTranscript,
    getTranscriptWithSpeakers,
  } = useMDTSpeech({ language: currentLanguage });

  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const processorRef = useRef(null);
  const timerRef = useRef(null);
  const transcriptContainerRef = useRef(null);

  // Timer effect
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRecording]);

  // Auto-scroll to bottom when new transcription is added
  useEffect(() => {
    if (
      transcriptContainerRef.current &&
      (segments.length > 0 || streamingSegment.text)
    ) {
      transcriptContainerRef.current.scrollTop =
        transcriptContainerRef.current.scrollHeight;
    }
  }, [segments, streamingSegment]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach((t) => t.stop());
        } catch (_) {}
        streamRef.current = null;
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close().catch(() => {});
        } catch (_) {}
        audioContextRef.current = null;
      }
      if (sourceRef.current) {
        sourceRef.current = null;
      }
      if (processorRef.current) {
        processorRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")} min`;
  };

  const startRecording = useCallback(async () => {
    let stream = null;
    let ctx = null;
    let source = null;
    let processor = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      ctx = new AudioContext({ sampleRate: 16000 });
      audioContextRef.current = ctx;
      source = ctx.createMediaStreamSource(stream);
      processor = ctx.createScriptProcessor(4096, 1, 1);
      sourceRef.current = source;
      processorRef.current = processor;

      await connectAndStart();

      processor.onaudioprocess = (e) => {
        const input = e.inputBuffer.getChannelData(0);
        const buffer = new ArrayBuffer(input.length * 2);
        const view = new DataView(buffer);
        for (let i = 0; i < input.length; i++) {
          const s = Math.max(-1, Math.min(1, input[i]));
          view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        }
        sendAudioChunk(buffer);
      };

      source.connect(processor);
      processor.connect(ctx.destination);
    } catch (err) {
      console.error("MDT start recording error:", err);
      toast.error(
        err.message ||
          t("mdt.connectionFailed") ||
          "Failed to start MDT recording",
      );
      if (stream) {
        try {
          stream.getTracks().forEach((t) => t.stop());
        } catch (_) {}
      }
      if (ctx) {
        try {
          ctx.close().catch(() => {});
        } catch (_) {}
      }
    }
  }, [connectAndStart, sendAudioChunk, t]);

  const endRecording = useCallback(async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
      if (sourceRef.current) {
        sourceRef.current = null;
      }
      if (processorRef.current) {
        processorRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      stopRecording();
    } catch (err) {
      console.error("MDT end recording error:", err);
      // Force cleanup even on error
      streamRef.current = null;
      audioContextRef.current = null;
      sourceRef.current = null;
      processorRef.current = null;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      stopRecording();
    }
  }, [stopRecording]);

  const handleGenerateReport = useCallback(async () => {
    const transcript = getTranscriptWithSpeakers();
    if (!transcript || !transcript.trim()) {
      toast.error(
        t("mdt.noTranscript") || "No transcript to generate report from.",
      );
      return;
    }

    setIsGenerating(true);
    try {
      const Aiobj = {
        StudyId: studyId,
        TemplateContent: "MDT discussion.",
        UserInputContent: transcript,
        TemplateInstructions: "",
        FindingsText: "",
        Language: currentLanguage?.split("-")[0] || "en",
        PromptTypeName: "reporting_ai_mdt_prompt",
      };
      const response = await processWithAi(Aiobj);
      const content =
        typeof response === "string"
          ? response
          : response?.payload?.content || "";
      if (content) {
        // Use AI response directly as-is (already HTML from AI)
        setGeneratedReport(content);
        toast.success(t("mdt.reportGenerated") || "MDT report generated.");
      } else {
        toast.error(t("mdt.reportFailed") || "Failed to generate MDT report.");
      }
    } catch (err) {
      console.error("MDT generate report error:", err);
      toast.error(
        err.message ||
          t("mdt.reportFailed") ||
          "Failed to generate MDT report.",
      );
    } finally {
      setIsGenerating(false);
    }
  }, [getTranscriptWithSpeakers, studyId, currentLanguage, t]);

  const handleInsertIntoReport = useCallback(() => {
    let toInsert = "";

    if (generatedReport && generatedReport.trim()) {
      // Use AI-generated HTML directly as-is
      toInsert = generatedReport.trim();
    } else {
      // Format transcript with separate <p> tags for each speaker segment
      const transcript = getTranscriptWithSpeakers();
      if (!transcript || !transcript.trim()) {
        toast.error(t("mdt.nothingToInsert") || "Nothing to insert.");
        return;
      }

      // Split by double newlines to get individual segments
      const transcriptSegments = transcript
        .split("\n\n")
        .filter((s) => s.trim());

      // Wrap each segment in <p> tags with HTML entity escaping for safety
      toInsert = transcriptSegments
        .map((segment) => {
          // Escape HTML entities to prevent issues
          const escaped = segment
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
          return `<p>${escaped}</p>`;
        })
        .join("");
    }

    if (!toInsert) {
      toast.error(t("mdt.nothingToInsert") || "Nothing to insert.");
      return;
    }

    if (typeof onInsertIntoReport === "function") {
      onInsertIntoReport(toInsert);
      toast.success(t("mdt.inserted") || "Inserted into report.");
    } else {
      toast.error(
        t("mdt.insertNotAvailable") || "Insert into report is not available.",
      );
    }
  }, [generatedReport, getTranscriptWithSpeakers, onInsertIntoReport, t]);

  const handleClear = useCallback(() => {
    clearTranscript();
    setGeneratedReport("");
    if (!isRecording) {
      setRecordingTime(0);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [clearTranscript, isRecording]);

  const hasContent =
    segments.length > 0 || (streamingSegment && streamingSegment.text);
  const hasReport = generatedReport && generatedReport.trim();

  return (
    <div className="mt-6 p-6 border dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t("mdt.recording") || "MDT Recording"}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t("mdt.subtitle") ||
                "Multi-speaker discussion with live speaker separation"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <LanguageDropdown
              value={currentLanguage}
              onChange={setCurrentLanguage}
              languages={DICTATION_LANGUAGES}
            />
            <button
              onClick={isRecording ? endRecording : startRecording}
              disabled={isConnecting}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                isRecording
                  ? "bg-green dark:bg-green-dark hover:bg-green-dark dark:hover:bg-green"
                  : "bg-red-500 hover:bg-red-600 dark:hover:bg-red-600"
              } text-black dark:text-white ${
                isConnecting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <img
                src={micwhite}
                alt="mic"
                className="w-4 h-4 invert dark:invert-0"
              />
              <span className="text-black dark:text-white">
                {isConnecting
                  ? t("mdt.connecting") || "Connecting…"
                  : isRecording
                  ? t("mdt.stop") || "Stop recording"
                  : t("mdt.start") || "Start MDT recording"}
              </span>
            </button>
            <div className="h-7 w-20 bg-[#EAEAEA] dark:bg-gray-700 place-content-center justify-items-center rounded-full flex items-center justify-center min-w-[80px]">
              <h1 className="text-xs font-semibold text-gray-900 dark:text-gray-200 text-center">
                {formatTime(recordingTime)}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Transcription Area */}
      <div className="mb-6">
        <div
          ref={transcriptContainerRef}
          className={`bg-gray-50 dark:bg-gray-900 rounded-lg p-4 min-h-[300px] max-h-[500px] overflow-y-auto ${
            !hasContent && !isRecording
              ? "flex items-center justify-center"
              : ""
          }`}
          dir={isRTL ? "rtl" : "ltr"}
          style={{
            textAlign: isRTL ? "right" : "left",
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          {!hasContent && !isRecording && (
            <p className="text-gray-500 dark:text-gray-400 text-center">
              {t("mdt.startPrompt") ||
                "Start MDT recording to see live transcription with speaker labels."}
            </p>
          )}
          {hasContent && (
            <div className="space-y-3" dir={isRTL ? "rtl" : "ltr"}>
              {segments.map((segment, idx) => {
                const speakerLabel =
                  segment.speaker != null
                    ? `Speaker ${segment.speaker}`
                    : "Speaker";
                return (
                  <div
                    key={idx}
                    className="mb-2"
                    dir={isRTL ? "rtl" : "ltr"}
                    style={{
                      textAlign: isRTL ? "right" : "left",
                      direction: isRTL ? "rtl" : "ltr",
                    }}
                  >
                    <span className="font-semibold text-gray-600 dark:text-gray-500">
                      {speakerLabel}:
                    </span>{" "}
                    <span className="text-gray-800 dark:text-gray-200">
                      {segment.text}
                    </span>
                  </div>
                );
              })}
              {streamingSegment.text && (
                <div
                  className="mb-2 opacity-70"
                  dir={isRTL ? "rtl" : "ltr"}
                  style={{
                    textAlign: isRTL ? "right" : "left",
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  <span className="font-semibold text-gray-600 dark:text-gray-500">
                    {streamingSegment.speaker != null
                      ? `Speaker ${streamingSegment.speaker}`
                      : "Speaker"}
                    :
                  </span>{" "}
                  <span className="text-gray-800 dark:text-gray-200">
                    {streamingSegment.text}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Generated Report Display */}
      {hasReport && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Generated MDT Report:
          </h3>
          <div
            className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 min-h-[200px] max-h-[400px] overflow-y-auto prose dark:prose-invert max-w-none"
            dir={isRTL ? "rtl" : "ltr"}
            style={{
              textAlign: isRTL ? "right" : "left",
              direction: isRTL ? "rtl" : "ltr",
            }}
            dangerouslySetInnerHTML={{ __html: generatedReport }}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={handleGenerateReport}
          disabled={!hasContent || isGenerating}
          className="px-4 py-2 rounded-lg bg-[#A4C639] hover:bg-[#CBEA7B80] text-gray-900 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating
            ? t("mdt.generating") || "Generating…"
            : t("mdt.generateReport") || "Generate MDT report"}
        </button>
        <button
          onClick={handleInsertIntoReport}
          disabled={!hasContent && !hasReport}
          className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("mdt.insertIntoReport") || "Insert into report"}
        </button>
        <button
          onClick={handleClear}
          disabled={isRecording}
          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("mdt.clear") || "Clear"}
        </button>
      </div>
    </div>
  );
}

export default MDTRecording;
